<script lang="ts">
import { defineComponent, PropType, ref, computed, watch, onMounted, nextTick } from 'vue'
import ToastMessageForLocalData from '@/components/toast/ToastMessageForLocalData.vue'
import { useErrorStore } from '@/stores/error'
import { useValidationStore } from '@/stores/validation'
import { useHeaderStore } from '@/stores/header'
import { useScreenLockStore } from '@/stores/screenLock'
import { hasOwnProperty } from '@/utilities/syntaxSugar'

export default defineComponent({
  name: 'ValidationObserver',

  components: {
    ToastMessageForLocalData,
  },

  props: {
    /**
     * form 内に存在する入力要素の name 属性を繋げた配列
     * @description バリデーション状況を管理する際の一覧として扱う
     */
    inputScheme: {
      type: Object as PropType<Inputs | Input>,
      default: () => ({}),
    },

    /**
     * バリデーションの名前空間
     * @description 自身に紐づく名前空間のバリデーションコールにのみ反応する。
     * @description 値は任意であるが、store に登録されるバリデーションキューの namespace の値と同一にする必要がある。
     * @description Form submit の場合、form の name 属性の値にする。
     */
    namespace: {
      type: String,
      default: '',
    },

    /**
     * バリデーション処理時に共通のスクリーンロックレイヤーの操作を行うかどうか
     * @description 基本は共通のスクリーンロックを行うが、
     *              モーダル内でバリデーションを行う際のように、
     *              モーダルですでにスクリーンロックを行っている場合、
     *              バリデーション処理によってそれが意図せず解除されてしまうため、
     *              そういった場合のための、共通のスクリーンロック側も操作するかどうかの制御フラグ。
     *              この値が false であったとしても、バリデーション処理中にスクリーン全体にロックレイヤー（ValidationObserver 自前のもの）がかかることは変わらない。
     *              よって、見た目の影響になる。（ValidationObserver 側の自前のロックレイヤーは透明なレイヤーなので、もし暗くしたい・半透明にしたい等の場合は、共通のスクリーンロック側で行う）
     */
    handleBackdrop: {
      type: Boolean,
      default: true,
    },

    /**
     * バリデーションエラー発生時にエラー箇所までスクロールするかどうか
     * @description デフォルトは true であるが、モーダル内でバリデーションを行う際のように、
     *              背景レイヤーが動いてしまうことを避けたい場合はこの値を false にする。
     */
    scrollByValidationError: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['error', 'valid', 'completed'],

  setup(props, { emit }) {
    const errorStore = useErrorStore()
    const validationStore = useValidationStore()
    const headerStore = useHeaderStore()
    const screenLock = useScreenLockStore()

    const toastEl = ref()
    const firstErrorDOM = ref()
    const inProgress = ref(false)

    /**
     * バリデーション設定されている input の name 配列
     */
    const validationInputNames = computed<string[]>(() => {
      if (!props.inputScheme) return []

      if (hasOwnProperty(props.inputScheme, 'name') && hasOwnProperty(props.inputScheme, 'value')) {
        // props で渡された inputScheme が単体の場合
        return (props.inputScheme.validationScheme && !!Object.values(props.inputScheme.validationScheme).filter(v => !!v).length)
          ? [ props.inputScheme.name ]
          : []
      } else {
        // props で渡された inputScheme が複数の Input オブジェクトからなる連想配列の場合
        return Object.values(props.inputScheme).filter(v => {
          // inputScheme は customField の非同期構築などで一時的に undefined エントリを含みうるためガードする
          if (!v) return false

          const validationScheme = v.validationScheme || {}
          // validation scheme が設定されているもののみ抽出
          return !!Object.values(validationScheme).filter(v => !!v).length
        }).map(v => v.name)
      }
    })

    const hasDebugger = computed(() => {
      const mode = new URL(window.location.href).searchParams.get('mode')
      return import.meta.env.MODE !== 'production' && mode === 'debug'
    })

    // このオブザーバーが管理しているバリデーションチェック状況を監視し、全てバリデーションチェックし終えたらバリデーションストップさせ、emit する
    watch(() => validationStore.getQueue(props.namespace)?.checkedFields.length, () => {
      if (validationStore.getQueue(props.namespace)?.checkedFields.length === validationInputNames.value.length) {
        validationStore.stopValidation(props.namespace)

        if (props.handleBackdrop) {
          screenLock.unlockScreen()
        }
        inProgress.value = false

        // すべてのバリデーションが OK だったら親に 'valid' の emit 通知を飛ばす
        if (!validationStore.getQueue(props.namespace)?.invalidFields.length) {
          emit('valid', props.namespace)
        } else {
          emit('error', props.namespace)

          toastEl.value.setToastItem({
            id: `validation-error-${new Date().getTime()}`,
            messageType: 'alert',
            message: '入力内容に不備があります',
            duration: 5,
          })

          scrollToFieldByValidationError()
        }

        validationStore.clearQueue(props.namespace)
        emit('completed', props.namespace)
      }
    })

    onMounted(() => {
      nextTick(() => {
        validationStore.init(props.namespace, validationInputNames.value)
      })
    })

    const init = () => {
      if (validationInputNames.value.length) {
        if (props.handleBackdrop) {
          screenLock.lockScreen({
            hasBlackOut: false,
            hasBlur: false,
            hasWaitAnimation: false,
            hasScrollLock: false,
          })
        }

        inProgress.value = true

        // バリデーション開始してなかったら諸々クリアしてバリデーション開始をキックする
        errorStore.clearErrors(() => {
          // 以下、store の error をクリアした後の callback 処理
          validationStore.clearQueue(props.namespace)
          validationStore.setQueue(props.namespace, [...validationInputNames.value])
        })
      } else {
        // バリデーションが何も設定されていなかったら即 'valid' の emit を通知する
        emit('valid', props.namespace)
      }
    }

    /**
     * バリデーションエラー発生時にエラー箇所までスクロール
     * （ウィンドウのスクロールのため、要素内スクロールには非対応）
     */
    const scrollToFieldByValidationError = (): void => {
      if (!props.scrollByValidationError) return

      // 取得済み error DOM の初期化
      firstErrorDOM.value = undefined

      /**
       * DOM の再レンダリングを待機してからエラー要素を拾って対象までスクロール
       */
      nextTick(() => {
        // 新たに発生したエラーの DOM を取得
        firstErrorDOM.value = document.querySelectorAll('[data-validation-error="true"]')[0]
        if (!firstErrorDOM.value) return

        const domPosition = firstErrorDOM.value.getBoundingClientRect().top + window.scrollY

        // 下スクロールだとヘッダーが消えるのでヘッダー分は加味しない
        const offset = -(domPosition < window.scrollY ? headerStore.context.height : 0)

        // Vue ScrollTo だと上手くスクロール発火できないため、window の scrollTo イベントで対応
        window.scrollTo({
          top: domPosition + offset,
          behavior: 'smooth'
        })
      })
    }

    return {
      // store
      validationStore,

      // data
      toastEl,
      inProgress,

      // computed
      hasDebugger,

      // methods
      init,
    }
  }
})
</script>

<template>
  <section
    class="f-section__validationObserver"
    :data-progress="inProgress">
    <div class="f-section__validationObserver--body">
      <slot />
      <ToastMessageForLocalData ref="toastEl" />
    <!-- /.f-section__validationObserver--body --></div>
  <!-- /.f-section__validationObserver --></section>

</template>

<style lang="sass" scoped>
.f-section__validationObserver
  $root: &

  &[data-progress="true"]
    #{$root}--body
      &::before
        content: ''
        width: 100%
        height: 100%
        display: block
        position: fixed
        top: 0
        left: 0
        z-index: 20 // same value as screen lock in Template components z-index

.f-section__debugger
  min-width: 240px
  max-height: 100%
  position: fixed
  bottom: 0
  left: 0
  overflow: auto
  color: $font-white
  background-color: rgba($bg-black, 0.5)
  z-index: 1000

  & &--body
    font-size: $font-xx-small
    padding: 1em

.o-list__debugger
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--term
    font-weight: bold

  & &--description
    padding-left: 1.5em
</style>
